import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';
import { withDevShareId, worklogApi } from './worklogApi';
import type { ApiResponse, FieldReportSubmitRequest, FieldReportSubmitResult } from '../types/worklog.types';
import type { CustReqApplySubmitItem, CustReqShareTarget } from '../types/custReqShare.types';

// 반장 작업보고 작성 화면의 고객 요청 반영 호출 — 공유 쿠키 화면(무인증). 경로가 `/work-order-shares/me` 로 시작해
// client.ts AUTH_EXEMPT_PATHS 에 걸린다. 근거: K1 3.4(A3)·3.8(E4).
//  GET  /worklog/work-order-shares/me                  : 안내 조회 응답의 reportTargets (기존 호출의 응답을 그대로 읽음)
//  POST /worklog/work-order-shares/me/field-reports    : 보고 제출 + 선택 파트 customerRequests

/**
 * 안내 조회 응답에서 반영 대상 요청을 읽는다. 서버가 작성 권한(작업자·기한 안)과 공정 확정 여부를 판단해
 * 대상이 없으면 빈 배열을 주므로 화면은 그대로 숨긴다. 식별자가 없는 원소는 버린다(값을 만들어 채우지 않음).
 */
export function readReportTargets(notice: unknown): CustReqShareTarget[] {
  const raw = (notice as { reportTargets?: unknown } | null | undefined)?.reportTargets;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is CustReqShareTarget => typeof r?.custReqId === 'number' && typeof r?.content === 'string')
    .map((r) => ({ ...r, photos: Array.isArray(r.photos) ? r.photos : [] }));
}

/**
 * 보고 제출 + 요청별 반영 입력. 반영 입력이 없으면 기존 제출 호출을 그대로 쓴다.
 * 있으면 기존 제출과 같은 본문에 선택 파트 `customerRequests`(JSON 문자열)만 더한다.
 */
export function submitFieldReportWithCustReq(
  body: FieldReportSubmitRequest,
  customerRequests: readonly CustReqApplySubmitItem[],
  signal?: AbortSignal,
) {
  if (customerRequests.length === 0) return worklogApi.submitFieldReportShare(body, signal);
  const form = new FormData();
  form.append('submitUuid', body.submitUuid);
  body.workTypeCodes.forEach((code) => form.append('workTypeCodes', code));
  if (body.workCmplYn) form.append('workCmplYn', body.workCmplYn);
  if (body.workDate) form.append('workDate', body.workDate);
  body.issueTypeCodes?.forEach((code) => form.append('issueTypeCodes', code));
  form.append('reportContent', body.reportContent);
  if (body.photos?.length) {
    form.append('photos', JSON.stringify(body.photos));
    body.photoFiles?.forEach((f) => form.append('photoFiles', f));
  }
  form.append('customerRequests', JSON.stringify(customerRequests));
  return apiClient.post<ApiResponse<FieldReportSubmitResult>>(
    withDevShareId(`${API_PRX.WORKLOG}/work-order-shares/me/field-reports`) as string,
    form,
    { signal },
  );
}
